import {
  AppBar,
  Accordion,
  AccordionSummary,
  IconButton,
  Toolbar,
  Typography,
  Dialog,
  Link,
  AccordionDetails,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React from 'react';
import {EnrollmentFileRejectEmailHistory} from 'Enrollment/models/EnrollmentFileRejectEmailHistory';
import ToolbarHeader, {ExportToolbarHeaderIcon, ToolbarHeaderIcon} from 'Shared/components/Toolbar/ToolbarHeader';
import HtmlPreview from 'Shared/components/HtmlPreview';
import {MailOutlineRounded, ReplyRounded} from '@material-ui/icons';
import './EnrollmentFileRejectsEmailHistoryContentDialog.sass';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import {toFormattedDateAsEst} from 'Shared/utilities/DateFormatter';
import {FileExtension} from 'Shared/components/FileDownloadLink';
import {useDispatch} from 'react-redux';
import {ModalState, showModal} from 'Core/ducks/modal';
import {getEnrollmentFileRejectsEmail} from 'Enrollment/ducks/enrollmentFileProcessingReject';

import {EnrollmentFileRejectDetailApplication} from 'Enrollment/models/EnrollmentFileRejectDetailApplication';

type EnrollmentFileRejectsEmailHistoryContentDialogProps = {
  dialogOpen: boolean;
  dialogClose: () => void;
  emailHistories: EnrollmentFileRejectEmailHistory[];
  applicationId: string;
  id: string | null | undefined;
  attachment: string | null | undefined;
  attachmentName: string | null | undefined;
  applicationSource: string | null | undefined;
};

const downloadAttachment = (applicationId: string, attachment: string, attachmentName: string) => {
  const fileName = `${attachmentName}`;
  const linkSource = `data:application/vnd.ms-excel;base64,${attachment}`;
  const downloadLink = document.createElement('a');
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};

const EnrollmentFileRejectsEmailHistoryContentDialog = (props: EnrollmentFileRejectsEmailHistoryContentDialogProps) => {
  const {applicationId, emailHistories, id, attachment, attachmentName, applicationSource} = props;

  const dispatch = useDispatch();
  const email = emailHistories?.find(emailHistory => emailHistory.id! === id)!;

  const handleEmailResend = () => {
    const enrollmentFileRejectDetailApplication: EnrollmentFileRejectDetailApplication = {
      applicationSource: applicationSource!, 
      id: applicationId!,
      applications: [],
    };
    dispatch(
      getEnrollmentFileRejectsEmail({
        rejectApplications: enrollmentFileRejectDetailApplication,
        isResend: true,
        emailHistoryId: email.id!,
      })
    );
    dispatch(
      showModal(
        ModalState.EnrollmentFileRejectsEmail({
          isResend: true,
          emailId: email.id!,
          groupId: email.groupId ? email.groupId : email.id!,
        })
      )
    );
  };

  return (
    <Dialog
      className="enrollment-file-reject-email-history"
      fullScreen
      open={props.dialogOpen}
      onClose={props.dialogClose}
    >
      <AppBar className="enrollment-file-reject-email-history__dialog-header">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={props.dialogClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className="enrollment-file-reject-email-history__dialog-header--title">
            Details
          </Typography>
        </Toolbar>
      </AppBar>
      <ModularHost>
        <ToolbarHeader title="Enrollment Processing Feedback E-mail">
          <ExportToolbarHeaderIcon
            title="Export E-mail Content"
            downloadedFileName={`E-mail_History_${id}`}
            extension={FileExtension.EML}
            relativeFileLocation={`/enrollmentFiles/processingHistory/rejects/${id}/email/content/export`}
            totalRecordCount = {0}
          />
          <ToolbarHeaderIcon title="Resend" onClick={handleEmailResend}>
            <ReplyRounded />
          </ToolbarHeaderIcon>
        </ToolbarHeader>
        <div className="parent-email-line">
          {email ? (
            <Accordion className="parent-email-line__expansion-panel">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div className="parent-email-line__expansion-panel__icon-container">
                  <MailOutlineRounded />
                </div>
                <div className="parent-email-line__expansion-panel__email-details">
                  <div className="parent-email-line__expansion-panel__email-details__content">
                    <div className="parent-email-line__expansion-panel__email-details__content__top">
                      <h3 className="parent-email-line__expansion-panel__email-details__content__top--subject">
                        {email.subject!}
                      </h3>
                      <p className="parent-email-line__expansion-panel__email-details__content__top--sent-date">
                        <span>{`Sent Date: ${toFormattedDateAsEst(email.sentDate!.toString())}`}</span>
                      </p>
                    </div>
                    <p className="parent-email-line__expansion-panel__email-details__content--field">
                      <span>{`From: ${email.from} (${email.firstName} ${email.lastName}, ${email.username})`}</span>
                    </p>
                    <p className="parent-email-line__expansion-panel__email-details__content--field">
                      {`To: ${email.to!.map(to => to!.email).join(', ')}`}
                    </p>
                    <p className="parent-email-line__expansion-panel__email-details__content--field">
                      {email.cc! ? `CC: ${email.cc!.map(cc => cc!.email).join(', ')}` : ''}
                    </p>
                    <Link
                      onClick={(event: {stopPropagation: () => void}) => {
                        event.stopPropagation();
                        downloadAttachment(applicationId!, attachment!, attachmentName!);
                      }}
                      variant="caption"
                      component="button"
                    >
                      <i className="fas fa-x fa-paperclip"> </i> Attachment
                    </Link>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div className="parent-email-line__expansion-panel__email-details">
                  <div className="parent-email-line__expansion-panel__email-details__content">
                    <hr className="parent-email-line__expansion-panel__email-details__content--content-separator" />
                    <HtmlPreview
                      className="parent-email-line__expansion-panel__email-details__content--body"
                      content={email.body!}
                    />
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          ) : (
            <></>
          )}
        </div>
      </ModularHost>
    </Dialog>
  );
};

export default EnrollmentFileRejectsEmailHistoryContentDialog;
